import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'phosphor-react';
import { cn } from '../utils/cn';
import { Button } from './Button';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

interface DialogContentProps {
  children: React.ReactNode;
  className?: string;
  onEscapeKeyDown?: (e: KeyboardEvent) => void;
  onPointerDownOutside?: (e: PointerEvent) => void;
}

interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

interface DialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

interface DialogDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
}

interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const Dialog: React.FC<DialogProps> = ({ open, onOpenChange, children }) => {
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/20 z-50"
            onClick={() => onOpenChange(false)}
          />
          
          {/* Dialog Container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            {React.Children.map(children, (child) => {
              if (React.isValidElement<DialogContentProps>(child)) {
                const childProps = child.props as DialogContentProps;
                return React.cloneElement(child, {
                  ...childProps,
                  onEscapeKeyDown: (e: KeyboardEvent) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      onOpenChange(false);
                    }
                    childProps.onEscapeKeyDown?.(e);
                  },
                  onPointerDownOutside: (e: PointerEvent) => {
                    e.stopPropagation();
                    onOpenChange(false);
                    childProps.onPointerDownOutside?.(e);
                  },
                });
              }
              return child;
            })}
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

const DialogContent: React.FC<DialogContentProps> = ({
  children,
  className,
  onEscapeKeyDown,
  onPointerDownOutside,
}) => {
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onEscapeKeyDown?.(e);
      }
    };

    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [onEscapeKeyDown]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "bg-background rounded-xl border border-border shadow-lg max-w-md w-full pointer-events-auto relative",
        className
      )}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) {
          onPointerDownOutside?.(e.nativeEvent as PointerEvent);
        }
      }}
    >
      {children}
    </motion.div>
  );
};

const DialogHeader: React.FC<DialogHeaderProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
      {...props}
    >
      {children}
    </div>
  );
};

const DialogTitle: React.FC<DialogTitleProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <h3
      className={cn(
        "font-sans font-semibold text-lg text-foreground tracking-[-0.18px]",
        className
      )}
      {...props}
    >
      {children}
    </h3>
  );
};

const DialogDescription: React.FC<DialogDescriptionProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <p
      className={cn(
        "font-sans font-normal text-sm text-text-subtle",
        className
      )}
      {...props}
    >
      {children}
    </p>
  );
};

const DialogFooter: React.FC<DialogFooterProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-3", className)}
      {...props}
    >
      {children}
    </div>
  );
};

interface DialogCloseProps {
  onClose: () => void;
  className?: string;
}

const DialogClose: React.FC<DialogCloseProps> = ({ onClose, className }) => {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClose}
      className={cn(
        "absolute right-4 top-4 z-10",
        className
      )}
      aria-label="Cerrar"
    >
      <X size={20} weight="bold" />
    </Button>
  );
};

export {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
};

